import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Cpu, HardDrive } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Checkbox,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useInstanceResize, useInstanceInfo } from '@/api/vmware/instances';
import type { VmwareInstance } from '@/api/vmware/instances';

/**
 * VMware resize modal — two tabs mirroring @console ManageOverview/index.tsx
 * VMwareResize and VMwareResizeDisk components.
 *
 * BUG FIX (field paths):
 *   Was (guessed): instance.metadata.resources.cpu / .memory / .cpu_hot_add_enabled
 *   Now (real):    instance.metadata.cpu.count / .memory.size_MiB / .cpu.hot_add_enabled
 *
 *   Was (isOn):    instance.status === 'POWERED_ON'  (provisioning status, always wrong)
 *   Now (isOn):    instance.metadata.power_state === 'POWERED_ON'
 *
 *   Disk capacity: disks[].value.capacity (bytes) ÷ 1024³ → GB
 *                  (was: metadata.disks[].capacity_gb which never existed)
 *
 * The /info endpoint returns the same vSphere metadata shape as the detail
 * endpoint's instance.metadata, so we keep useInstanceInfo as a fresher fallback
 * but always try the detail instance first.
 */
export function InstanceResize({ serviceId, instance }: { serviceId: string; instance: VmwareInstance }) {
  const navigate = useNavigate();
  const { instanceId } = useParams<{ instanceId: string }>();
  const base = `/services/compute/${serviceId}/instances/${instanceId}`;
  const close = () => navigate(base);

  const [tab, setTab] = useState<'configuration' | 'disk'>('configuration');

  return (
    <Dialog open onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Resize instance</DialogTitle>
        </DialogHeader>

        {/* Tab switcher */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--vn-border-default)', padding: '0 var(--vn-space-4)' }}>
          {(['configuration', 'disk'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: 'var(--vn-space-2) var(--vn-space-3)',
                fontWeight: tab === t ? 600 : 400,
                borderBottom: tab === t ? '2px solid var(--vn-accent-solid)' : '2px solid transparent',
                color: tab === t ? 'var(--vn-fg-default)' : 'var(--vn-fg-muted)',
                fontSize: 'var(--vn-font-size-sm)',
                marginBottom: -1,
              }}
            >
              {t === 'configuration' ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Cpu size={14} /> CPU &amp; Memory</span>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><HardDrive size={14} /> Disk</span>
              )}
            </button>
          ))}
        </div>

        {tab === 'configuration' ? (
          <ConfigurationResize serviceId={serviceId} instance={instance} instanceId={instanceId!} onClose={close} />
        ) : (
          <DiskResize serviceId={serviceId} instance={instance} instanceId={instanceId!} onClose={close} />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---- CPU / RAM resize -------------------------------------------------------

const CPU_OPTIONS = [2, 4, 6, 8, 16, 32, 64, 128];
const MEM_OPTIONS_GB = [2, 4, 6, 8, 16, 32, 64, 128];

function ConfigurationResize({
  serviceId,
  instance,
  instanceId,
  onClose,
}: {
  serviceId: string;
  instance: VmwareInstance;
  instanceId: string;
  onClose: () => void;
}) {
  const { show } = useToast();

  // /info endpoint returns the same vSphere metadata shape; use as a fresher
  // fallback for cpu/memory values when available.
  const { data: info } = useInstanceInfo(serviceId, instanceId);
  const infoRecord = info as Record<string, unknown> | undefined;

  // Real CPU path: metadata.cpu.count / .hot_add_enabled
  // /info may expose the same structure under info.cpu.* (same vSphere proxy).
  const currentCpu =
    (infoRecord?.cpu as { count?: number } | undefined)?.count ??
    instance.metadata?.cpu?.count ?? 0;

  // Real memory path: metadata.memory.size_MiB
  const currentMemMiB =
    (infoRecord?.memory as { size_MiB?: number } | undefined)?.size_MiB ??
    instance.metadata?.memory?.size_MiB ?? 0;

  // Hot-add flags: metadata.cpu.hot_add_enabled / metadata.memory.hot_add_enabled
  const cpuHotAdd =
    (infoRecord?.cpu as { hot_add_enabled?: boolean } | undefined)?.hot_add_enabled ??
    instance.metadata?.cpu?.hot_add_enabled ?? false;
  const memHotAdd =
    (infoRecord?.memory as { hot_add_enabled?: boolean } | undefined)?.hot_add_enabled ??
    instance.metadata?.memory?.hot_add_enabled ?? false;

  const currentMemGb = Math.round(currentMemMiB / 1024);

  // Power state: metadata.power_state (NOT instance.status which is provisioning).
  const isOn = instance.metadata?.power_state === 'POWERED_ON';

  const [numCPU, setNumCPU] = useState(currentCpu);
  const [memoryGB, setMemoryGB] = useState(currentMemGb);
  const [cpuHotAddEnabled, setCpuHotAddEnabled] = useState(cpuHotAdd);
  const [memHotAddEnabled, setMemHotAddEnabled] = useState(memHotAdd);
  const [understood, setUnderstood] = useState(false);

  const resize = useInstanceResize(serviceId, instanceId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Configuration update requested' });
      onClose();
    },
    onError: () => show({ tone: 'danger', title: 'Resize failed' }),
  });

  const busy = resize.isPending;
  const isDirty = numCPU !== currentCpu || memoryGB !== currentMemGb
    || cpuHotAddEnabled !== cpuHotAdd || memHotAddEnabled !== memHotAdd;

  const handleSubmit = () => {
    if (!isDirty) return;
    resize.mutate({
      numCPU,
      cpuHotAddEnabled,
      memoryMB: memoryGB * 1024,
      memoryHotAddEnabled: memHotAddEnabled,
    });
  };

  return (
    <>
      <DialogBody>
        <p style={{ marginTop: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
          Current: <b>{currentCpu} vCPU</b> · <b>{currentMemGb} GB memory</b>. Select new values below.
        </p>

        <Alert tone="info" style={{ marginBottom: 'var(--vn-space-3)' }}>
          To adjust resources without Hot Plug enabled, powering off the VM is required.
        </Alert>

        {/* CPU */}
        <div style={{ marginBottom: 'var(--vn-space-3)' }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', marginBottom: 'var(--vn-space-1)' }}>
            CPU (vCore)
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--vn-space-2)', marginBottom: 'var(--vn-space-2)' }}>
            {CPU_OPTIONS.map((v) => {
              // Mirror @console: when on + no hot-add, cannot decrease below current.
              const cannotDecrease = isOn && !cpuHotAdd && v < currentCpu;
              const disabledWhenOn = isOn && !cpuHotAdd;
              const isActive = numCPU === v;
              return (
                <button
                  key={v}
                  disabled={disabledWhenOn || cannotDecrease || busy}
                  onClick={() => setNumCPU(v)}
                  style={presetButtonStyle(isActive, disabledWhenOn || cannotDecrease)}
                >
                  {v}
                </button>
              );
            })}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)' }}>
            <Checkbox
              checked={cpuHotAddEnabled}
              onCheckedChange={(c) => setCpuHotAddEnabled(c === true)}
              disabled={isOn || busy}
            />
            CPU Hot Plug
          </label>
        </div>

        {/* Memory */}
        <div style={{ marginBottom: 'var(--vn-space-3)' }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', marginBottom: 'var(--vn-space-1)' }}>
            Memory (GB)
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--vn-space-2)', marginBottom: 'var(--vn-space-2)' }}>
            {MEM_OPTIONS_GB.map((v) => {
              const cannotDecrease = isOn && !memHotAdd && v < currentMemGb;
              const disabledWhenOn = isOn && !memHotAdd;
              const isActive = memoryGB === v;
              return (
                <button
                  key={v}
                  disabled={disabledWhenOn || cannotDecrease || busy}
                  onClick={() => setMemoryGB(v)}
                  style={presetButtonStyle(isActive, disabledWhenOn || cannotDecrease)}
                >
                  {v}
                </button>
              );
            })}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)' }}>
            <Checkbox
              checked={memHotAddEnabled}
              onCheckedChange={(c) => setMemHotAddEnabled(c === true)}
              disabled={isOn || busy}
            />
            Memory Hot Plug
          </label>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)', marginTop: 'var(--vn-space-3)' }}>
          <Checkbox checked={understood} onCheckedChange={(c) => setUnderstood(c === true)} disabled={busy} />
          I understand: disk size cannot be decreased once increased, and a restart may be required.
        </label>
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          startIcon={<Cpu size={15} />}
          onClick={handleSubmit}
          disabled={busy || !isDirty || !understood}
          loading={busy}
        >
          {busy ? 'Applying…' : 'Update configuration'}
        </Button>
      </DialogFooter>
    </>
  );
}

// ---- Per-disk resize --------------------------------------------------------

const DISK_PRESETS_GB = [50, 100, 250, 500];
const DISK_UNIT_PRICE = 2500; // VND/GB/month (display only)

interface DiskEntry {
  key: string;
  index: number;
  /** Current size in GB. */
  capacityGb: number;
}

function DiskResize({
  serviceId,
  instance,
  instanceId,
  onClose,
}: {
  serviceId: string;
  instance: VmwareInstance;
  instanceId: string;
  onClose: () => void;
}) {
  const { show } = useToast();

  const { data: info } = useInstanceInfo(serviceId, instanceId);
  const infoRecord = info as Record<string, unknown> | undefined;

  // Real disk path: metadata.disks = [{ key, value: { capacity (bytes), label, type } }]
  // /info may expose the same structure. Prefer info if available (fresher).
  const disks: DiskEntry[] = (() => {
    const rawInfo = infoRecord?.disks;
    if (Array.isArray(rawInfo) && rawInfo.length > 0) {
      return (rawInfo as Array<{ key: unknown; value?: { capacity?: number; label?: string } }>)
        .sort((a, b) => Number(a.key) - Number(b.key))
        .map((d, i) => ({
          key: String(d.key),
          index: i,
          // capacity in bytes → GB
          capacityGb: Math.round(Number(d.value?.capacity ?? 0) / 1024 ** 3),
        }));
    }
    // Fallback to detail instance.metadata.disks (real path with .value.capacity in bytes).
    const metaDisks = instance.metadata?.disks ?? [];
    return metaDisks
      .slice()
      .sort((a, b) => Number(a.key) - Number(b.key))
      .map((d, i) => ({
        key: String(d.key),
        index: i,
        capacityGb: Math.round(Number(d.value?.capacity ?? 0) / 1024 ** 3),
      }));
  })();

  const [selectedKey, setSelectedKey] = useState<string | null>(disks[0]?.key ?? null);
  const [preset, setPreset] = useState<number | 'custom' | null>(null);
  const [customGb, setCustomGb] = useState('');

  const selectedDisk = disks.find((d) => d.key === selectedKey) ?? disks[0];
  const newSize =
    preset === 'custom'
      ? parseInt(customGb, 10) || 0
      : preset ?? 0;

  const canSubmit = selectedDisk != null && newSize > selectedDisk.capacityGb;

  const resize = useInstanceResize(serviceId, instanceId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Disk resize requested' });
      onClose();
    },
    onError: () => show({ tone: 'danger', title: 'Disk resize failed' }),
  });

  const busy = resize.isPending;

  const handleSubmit = () => {
    if (!canSubmit || !selectedDisk) return;
    resize.mutate({
      disks: [{ key: Number(selectedDisk.key), capacity: newSize }],
    });
  };

  if (disks.length === 0) {
    return (
      <DialogBody>
        <p style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
          No disks found for this instance.
        </p>
      </DialogBody>
    );
  }

  return (
    <>
      <DialogBody>
        <p style={{ marginTop: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
          Select a disk then choose its new size. Disk size can only be increased.
        </p>

        {disks.length > 1 && (
          <div style={{ marginBottom: 'var(--vn-space-3)' }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', marginBottom: 'var(--vn-space-1)' }}>
              Disk
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
              {disks.map((d) => (
                <button
                  key={d.key}
                  onClick={() => { setSelectedKey(d.key); setPreset(null); setCustomGb(''); }}
                  style={presetButtonStyle(selectedKey === d.key, false)}
                >
                  disk-{d.index} ({d.capacityGb} GB)
                </button>
              ))}
            </div>
          </div>
        )}

        {selectedDisk && (
          <>
            <div style={{ marginBottom: 'var(--vn-space-3)' }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', marginBottom: 'var(--vn-space-1)' }}>
                Select disk size — disk-{selectedDisk.index}
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--vn-space-2)', marginBottom: 'var(--vn-space-2)' }}>
                {DISK_PRESETS_GB.map((gb) => {
                  const tooSmall = gb <= selectedDisk.capacityGb;
                  const isActive = preset === gb;
                  return (
                    <button
                      key={gb}
                      disabled={tooSmall || busy}
                      onClick={() => { if (!tooSmall) { setPreset(gb); setCustomGb(''); } }}
                      style={presetButtonStyle(isActive, tooSmall)}
                    >
                      <div style={{ fontWeight: 600 }}>{gb} GB</div>
                      <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                        {(DISK_UNIT_PRICE * gb).toLocaleString()}/month
                      </div>
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setPreset('custom')}
                style={{ ...presetButtonStyle(preset === 'custom', false), width: '100%', textAlign: 'left', padding: 'var(--vn-space-3)' }}
              >
                <div style={{ fontWeight: 600, marginBottom: preset === 'custom' ? 'var(--vn-space-2)' : 0 }}>
                  Custom (GB)
                </div>
                {preset === 'custom' && (
                  <Input
                    type="number"
                    min={selectedDisk.capacityGb + 1}
                    placeholder={`> ${selectedDisk.capacityGb}`}
                    value={customGb}
                    onChange={(e) => setCustomGb(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={busy}
                    style={{ width: '100%' }}
                  />
                )}
              </button>
            </div>

            <Alert tone="warning">
              Once you increase disk size, it cannot be reduced. The minimum is <b>{selectedDisk.capacityGb} GB</b>.
            </Alert>
          </>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          startIcon={<HardDrive size={15} />}
          onClick={handleSubmit}
          disabled={busy || !canSubmit}
          loading={busy}
        >
          {busy ? 'Applying…' : 'Resize disk'}
        </Button>
      </DialogFooter>
    </>
  );
}

// ---- Shared style helpers ---------------------------------------------------

function presetButtonStyle(isActive: boolean, isDisabled: boolean): React.CSSProperties {
  return {
    border: `1px solid ${isActive ? 'var(--vn-accent-solid)' : 'var(--vn-border-default)'}`,
    borderRadius: 'var(--vn-radius-sm)',
    background: isActive ? 'var(--vn-accent-subtle, var(--vn-bg-subtle))' : 'var(--vn-bg-surface)',
    color: isDisabled ? 'var(--vn-fg-subtle)' : isActive ? 'var(--vn-accent-text, var(--vn-fg-default))' : 'var(--vn-fg-default)',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.45 : 1,
    padding: 'var(--vn-space-2)',
    fontSize: 'var(--vn-font-size-sm)',
    textAlign: 'center' as const,
  };
}
